import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ExternalLink, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { api } from '../../../../api/client';

const TONE_CLASS = {
  emerald: 'text-emerald-700 bg-emerald-50',
  rose: 'text-rose-700 bg-rose-50',
  amber: 'text-amber-700 bg-amber-50',
};

export default function ContentQualityFix({ domain, onFixApplied }) {
  const pagesQuery = useQuery({
    queryKey: ['pages', domain._id],
    queryFn: () => api.get(`/api/domains/${domain._id}/pages`).then((r) => r.data.pages),
  });

  if (pagesQuery.isPending) {
    return <div className="text-sm text-ink-400">Loading pages…</div>;
  }
  const pages = pagesQuery.data || [];
  const worst = pages
    .filter((p) => (p.audit_breakdown?.contentQuality ?? 0) < 3)
    .sort(
      (a, b) =>
        (a.audit_breakdown?.contentQuality ?? 0) -
        (b.audit_breakdown?.contentQuality ?? 0),
    )
    .slice(0, 8);

  if (worst.length === 0) {
    return (
      <div className="text-sm text-ink-500">
        No pages currently fail this check. Re-run the audit if you’ve recently added
        author metadata or published dates.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-600">
        AI engines reward pages with a clear author, a publish date, and substantive
        content. Each of these signals adds points; pages below 3 points need at least one
        of them.
      </p>

      <div className="space-y-2">
        {worst.map((p) => {
          const sig = p.audit_quality_signals || {};
          let pathLabel = p.url;
          try {
            pathLabel = new URL(p.url).pathname || p.url;
          } catch {
            // leave as-is
          }
          const wordCountTone =
            (sig.wordCount || 0) >= 800
              ? { tone: 'emerald', icon: CheckCircle2, label: 'Plenty' }
              : (sig.wordCount || 0) >= 300
                ? { tone: 'amber', icon: AlertTriangle, label: 'Thin' }
                : { tone: 'rose', icon: XCircle, label: 'Very thin' };
          return (
            <div key={p._id} className="rounded-xl border border-gray-100 p-3">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 text-sm font-medium text-ink-900">
                    <span className="truncate">{p.title || pathLabel}</span>
                    <a
                      href={p.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-ink-400 hover:text-brand-600 flex-none"
                      title="Open page"
                    >
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  <div className="text-xs text-ink-400 truncate">{p.url}</div>
                </div>
                <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md flex-none">
                  {p.audit_breakdown?.contentQuality ?? 0} pts
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                <span
                  className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md font-medium ${TONE_CLASS[wordCountTone.tone]}`}
                >
                  <wordCountTone.icon className="w-3 h-3" />
                  Words: {sig.wordCount || 0} — {wordCountTone.label}
                </span>
                <span
                  className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md font-medium ${
                    sig.hasDate ? TONE_CLASS.emerald : TONE_CLASS.rose
                  }`}
                >
                  {sig.hasDate ? (
                    <CheckCircle2 className="w-3 h-3" />
                  ) : (
                    <XCircle className="w-3 h-3" />
                  )}
                  Date: {sig.hasDate ? 'present' : 'missing'}
                </span>
                <span
                  className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md font-medium ${
                    sig.hasAuthor ? TONE_CLASS.emerald : TONE_CLASS.rose
                  }`}
                >
                  {sig.hasAuthor ? (
                    <CheckCircle2 className="w-3 h-3" />
                  ) : (
                    <XCircle className="w-3 h-3" />
                  )}
                  Author: {sig.hasAuthor ? 'present' : 'missing'}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <details>
        <summary className="cursor-pointer text-xs text-emerald-800 hover:text-emerald-900 font-medium select-none">
          How to fix content quality
        </summary>
        <ol className="list-decimal pl-5 mt-2 space-y-1.5 text-xs text-ink-600 bg-emerald-50/60 border border-emerald-100 rounded-lg p-3">
          <li>
            <strong>Word count:</strong> aim for 600+ words on cornerstone pages. Thin pages
            don’t give AI enough to extract.
          </li>
          <li>
            <strong>Date:</strong> add a <code className="px-1 bg-white border border-emerald-200 rounded text-[10px]">&lt;time&gt;</code> element or{' '}
            <code className="px-1 bg-white border border-emerald-200 rounded text-[10px]">&lt;meta property=&quot;article:published_time&quot;&gt;</code> in your page head.
          </li>
          <li>
            <strong>Author:</strong> add{' '}
            <code className="px-1 bg-white border border-emerald-200 rounded text-[10px]">&lt;meta name=&quot;author&quot;&gt;</code> or a visible byline with{' '}
            <code className="px-1 bg-white border border-emerald-200 rounded text-[10px]">rel=&quot;author&quot;</code>. The Content tab Rewrite flow also lets you set author per-page.
          </li>
        </ol>
      </details>

      <div className="flex justify-between items-center pt-2 border-t border-gray-100 gap-2 flex-wrap">
        <Link to="/content" className="text-xs text-brand-600 hover:underline">
          Open Content tab to rewrite these pages →
        </Link>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => onFixApplied?.()}
        >
          I’ve made the changes
        </button>
      </div>
    </div>
  );
}
